import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Phone, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_emergency-locksmith-4/artifacts/dcw292bm_ChatGPT%20Image%20Mar%2027%2C%202026%2C%2010_11_56%20PM.png";
const PHONE_NUMBER = "306-262-5995";

const Header = ({ onQuoteClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/services", label: "Services" },
    { href: "/about", label: "About" },
    { href: "/contact", label: "Contact" },
  ];

  const showSolidBg = isScrolled || !isHomePage;

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showSolidBg
          ? "bg-slate-900 border-b border-slate-700 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="306 Locksmith"
              className="h-12 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-sm font-semibold uppercase tracking-wider transition-colors text-white hover:text-amber-400"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="flex items-center gap-3">
            {/* Phone Button - Always Visible */}
            <a
              href={`tel:${PHONE_NUMBER.replace(/-/g, "")}`}
              className="flex items-center"
              data-testid="phone-cta"
            >
              <Button
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold rounded-sm px-4 py-2 md:px-6 md:py-3 pulse-button"
                size="lg"
              >
                <Phone className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                <span className="hidden sm:inline phone-number">{PHONE_NUMBER}</span>
                <span className="sm:hidden">Call Now</span>
              </Button>
            </a>

            {/* Quote Button - Desktop */}
            <Link to="/contact">
              <Button
                variant="outline"
                className="hidden md:flex font-semibold rounded-sm border-white text-white hover:bg-white hover:text-slate-900"
                data-testid="quote-button"
              >
                Free Quote
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-white" />
              ) : (
                <Menu className="w-6 h-6 text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-800 border-t border-slate-700 py-4" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-white font-semibold uppercase tracking-wider px-4 py-2 hover:bg-slate-700"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Button className="mx-4 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-sm">
                  Get Free Quote
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
